// ============================================================================
// FILE: src/components/Properties/RectangleProperties/RectangleProperties.tsx
// Updated to match ConnectorProperties UI style
// ============================================================================

import React from 'react';
import { Type, Palette, Box as BoxIcon, Trash2 } from 'lucide-react';
import { useScene } from 'src/hooks/useScene';
import { useRectangle } from 'src/hooks/useRectangle';
import { useUiStateStore } from '@stores/uiStateStore';
import {
  rectangleLabelPositionOptions,
  rectangleStyleOptions
} from 'src/schemas/rectangle';
import type {
  RectangleLabelPosition,
  RectangleStyle
} from 'src/schemas/rectangle';
import { ColorPicker } from '../ColorPicker';

interface Props {
  id: string;
}

export const RectangleProperties: React.FC<Props> = ({ id }) => {
  const { updateRectangle, deleteRectangle } = useScene();
  const rectangle = useRectangle(id);
  const uiStateActions = useUiStateStore((state) => state.actions);

  if (!rectangle) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Rectangle not found
        </p>
      </div>
    );
  }

  const handleDelete = () => {
    if (confirm('Delete this rectangle?')) {
      uiStateActions.setItemControls(null);
      deleteRectangle(rectangle.id);
    }
  };

  return (
    <div className="space-y-6 p-4">
      {/* Description Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Type className="h-4 w-4 text-gray-500" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Description
          </label>
        </div>

        <div>
          <textarea
            value={rectangle.description || ''}
            onChange={(e) =>
              updateRectangle(rectangle.id, { description: e.target.value })
            }
            rows={3}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white dark:placeholder-slate-400"
            placeholder="Describe what this rectangle represents..."
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Add a description to help others understand this area
          </p>
        </div>
      </div>

      {/* Label Position - Only show if description exists */}
      {rectangle.description && rectangle.description.trim() !== '' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
            Label Position
          </label>
          <div className="grid grid-cols-3 gap-2">
            {rectangleLabelPositionOptions.map((position) => (
              <button
                key={position}
                onClick={() =>
                  updateRectangle(rectangle.id, {
                    labelPosition: position as RectangleLabelPosition
                  })
                }
                className={`rounded px-2 py-2 text-xs font-medium transition-colors ${
                  (rectangle.labelPosition || 'CENTER') === position
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-gray-300'
                }`}
                title={position.replace('_', ' ')}
              >
                {position === 'CENTER'
                  ? '⊡'
                  : position === 'TOP_LEFT'
                    ? '◤'
                    : position === 'TOP_RIGHT'
                      ? '◥'
                      : position === 'BOTTOM_LEFT'
                        ? '◣'
                        : '◢'}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Choose where the label appears on the rectangle
          </p>
        </div>
      )}

      {/* Appearance Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-gray-500" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Appearance
          </label>
        </div>

        {/* Fill Toggle */}
        <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-slate-600 dark:bg-slate-800">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Fill Rectangle
          </label>
          <button
            onClick={() =>
              updateRectangle(rectangle.id, { fill: !(rectangle.fill ?? true) })
            }
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              (rectangle.fill ?? true)
                ? 'bg-blue-500'
                : 'bg-gray-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                (rectangle.fill ?? true) ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Fill Color */}
        {(rectangle.fill ?? true) && (
          <div className="">
            <ColorPicker
              value={rectangle.color || '#3b82f6'}
              onChange={(e: any) => updateRectangle(rectangle.id, { color: e })}
              label="Color"
            />
          </div>
        )}

        {/* Opacity */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
            Opacity: {Math.round((rectangle.opacity ?? 0.3) * 100)}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={(rectangle.opacity ?? 0.3) * 100}
            onChange={(e) =>
              updateRectangle(rectangle.id, {
                opacity: parseInt(e.target.value) / 100
              })
            }
            className="w-full"
          />
        </div>
      </div>

      {/* Style & Border Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <BoxIcon className="h-4 w-4 text-gray-500" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Style & Border
          </label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Border Style */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              Border Style
            </label>
            <select
              value={rectangle.borderStyle || 'SOLID'}
              onChange={(e) =>
                updateRectangle(rectangle.id, {
                  borderStyle: e.target.value as RectangleStyle
                })
              }
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
            >
              {rectangleStyleOptions.map((style) => (
                <option key={style} value={style}>
                  {style.charAt(0) + style.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </div>

          {/* Border Color */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              Border Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={rectangle.borderColor || rectangle.color || '#3b82f6'}
                onChange={(e) =>
                  updateRectangle(rectangle.id, { borderColor: e.target.value })
                }
                className="h-10 w-16 rounded border border-gray-300 dark:border-slate-600"
              />
              <span className="text-xs text-gray-600 dark:text-gray-400">
                {rectangle.borderColor ? rectangle.borderColor : 'Auto'}
              </span>
            </div>
          </div>
        </div>

        {/* Border Width - Full Row */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
            Border Width: {rectangle.borderWidth ?? 2}px
          </label>
          <input
            type="range"
            min="0"
            max="10"
            step="1"
            value={rectangle.borderWidth ?? 2}
            onChange={(e) =>
              updateRectangle(rectangle.id, {
                borderWidth: parseInt(e.target.value)
              })
            }
            className="w-full"
          />
        </div>

        {/* Corner Radius - Full Row */}
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
            Corner Radius: {rectangle.cornerRadius ?? 22}px
          </label>
          <input
            type="range"
            min="0"
            max="50"
            step="1"
            value={rectangle.cornerRadius ?? 22}
            onChange={(e) =>
              updateRectangle(rectangle.id, {
                cornerRadius: parseInt(e.target.value)
              })
            }
            className="w-full"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="border-t border-gray-200 pt-4 dark:border-slate-600">
        <button
          onClick={handleDelete}
          className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </button>
      </div>
    </div>
  );
};
